import { Fragment, useState } from 'react';

import type { ApPaymentWithAllocations } from '@dcc-erp/shared';
import { fmt, fmtDate, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { useApPayments } from '../../lib/queries/ap';
import { exportRows, todayStamp, type ExportColumn } from '../../lib/xlsx';

// =============================================================================
// Payments-history tab — wires useApPayments with an optional per-vendor +
// date-range filter. Drill-through to a payment's allocations is a row-expand.
// =============================================================================
export interface ApPaymentsTabProps {
  companyId: string | undefined;
  vendors: { id: string; code: string; nameTh: string }[];
  companyCode: string;
  // M7 Phase 1b — re-open a payment's engine 4-eyes panel from the history tab
  // (DRAFT/PENDING/RETURNED rows). Set by ApPage to mount ApPaymentApprovalPanel.
  onOpenPanel: (payment: ApPaymentWithAllocations) => void;
}

export function ApPaymentsTab({
  companyId,
  vendors,
  companyCode,
  onOpenPanel,
}: ApPaymentsTabProps) {
  const [vendorId, setVendorId] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const { data: payments = [] } = useApPayments(companyId, {
    ...(vendorId ? { vendorId } : {}),
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
  });
  const [expanded, setExpanded] = useState<string | null>(null);

  async function handleExport() {
    const cols: ExportColumn<ApPaymentWithAllocations>[] = [
      { header: 'voucherNo', value: (p) => p.voucherNo, width: 16 },
      { header: 'paymentDate', value: (p) => String(p.paymentDate).slice(0, 10), width: 12 },
      { header: 'vendorName', value: (p) => p.vendorName, width: 28 },
      { header: 'cashAccountCode', value: (p) => p.cashAccountCode, width: 14 },
      { header: 'totalAmount', value: (p) => Number(p.totalAmount), width: 14 },
      { header: 'allocations', value: (p) => p.allocations.length, width: 10 },
    ];
    await exportRows(
      `การจ่ายเจ้าหนี้_${companyCode}_${todayStamp()}.xlsx`,
      'การจ่ายเจ้าหนี้',
      cols,
      payments,
    );
  }

  return (
    <div className="tbl-shell">
      <div className="filter-row" style={{ gap: 12, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
          <span className="muted">ผู้ขาย</span>
          <select
            className="select"
            aria-label="ผู้ขาย"
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            style={{ height: 34, fontSize: 13, minWidth: 200 }}
          >
            <option value="">— ทุกผู้ขาย —</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.code} · {v.nameTh}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
          <span className="muted">ตั้งแต่</span>
          <input
            type="date"
            className="input"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{ height: 34, fontSize: 13 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12 }}>
          <span className="muted">ถึง</span>
          <input
            type="date"
            className="input"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{ height: 34, fontSize: 13 }}
          />
        </label>
        <button
          className="btn"
          onClick={() => void handleExport()}
          disabled={payments.length === 0}
          style={{ marginLeft: 'auto', alignSelf: 'flex-end' }}
        >
          <Icon name="download" size={14} /> ส่งออก
        </button>
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 480 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่ใบจ่าย</th>
              <th>วันที่</th>
              <th>ผู้ขาย</th>
              <th>บัญชีจ่าย</th>
              <th className="num">จำนวนเงิน</th>
              <th className="num">รายการจัดสรร</th>
              <th>สถานะ</th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 && (
              <tr>
                <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่พบประวัติการจ่าย
                </td>
              </tr>
            )}
            {payments.map((p) => {
              // M7 1b — "live" == journalEntryId IS NOT NULL (DECISION-Z: no
              // accounting-status enum). A payment still in the engine
              // (DRAFT/PENDING/RETURNED, not yet posted) can be re-opened for
              // submit/approve via onOpenPanel.
              const live = !!p.journalEntryId;
              const inEngine =
                !live &&
                (p.approvalStatus === 'DRAFT' ||
                  p.approvalStatus === 'PENDING' ||
                  p.approvalStatus === 'RETURNED');
              return (
                <Fragment key={p.id}>
                  <tr
                    style={{ cursor: 'pointer' }}
                    onClick={() => setExpanded((e) => (e === p.id ? null : p.id))}
                  >
                    <td className="code">{p.voucherNo}</td>
                    <td>{fmtDate(p.paymentDate)}</td>
                    <td>{p.vendorName}</td>
                    <td className="code">{p.cashAccountCode}</td>
                    <td className="num tnum">{fmt(toNumber(p.totalAmount), 2)}</td>
                    <td className="num tnum">{p.allocations.length}</td>
                    <td>
                      {live ? (
                        <span className="badge positive">
                          <span className="bdot" />
                          จ่ายแล้ว
                        </span>
                      ) : p.approvalStatus === 'PENDING' ? (
                        <span className="badge warning">
                          <span className="bdot" />
                          รออนุมัติ
                        </span>
                      ) : p.approvalStatus === 'RETURNED' ? (
                        <span className="badge negative">
                          <span className="bdot" />
                          ตีกลับ
                        </span>
                      ) : (
                        <span className="badge info">
                          <span className="bdot" />
                          ร่าง
                        </span>
                      )}{' '}
                      {inEngine && (
                        <button
                          type="button"
                          className="btn ghost"
                          data-testid={`payment-open-panel-${p.id}`}
                          style={{ marginLeft: 4, padding: '2px 8px', fontSize: 12 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenPanel(p);
                          }}
                        >
                          อนุมัติ
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === p.id && (
                    <tr>
                      <td colSpan={7} style={{ background: '#fafafa' }}>
                        <div style={{ padding: '8px 12px', fontSize: 13 }}>
                          {p.allocations.map((a) => (
                            <div key={a.id} className="muted">
                              บิล {a.billId.slice(0, 8)}… · {fmt(toNumber(a.amount), 2)} บาท
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
